import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import PATH_PAGE from '@/router/path';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import { Link } from 'react-router-dom';
import { PlusIcon, Server, RefreshCw, Loader2, ChevronRight, ChevronDown } from 'lucide-react';
import { useListSSHInstances } from '@/service/gpu-instance/hook';
import { SSHInstance, SSHInstanceStatus, SSHInstanceStatusLabel, InstanceTag, InstanceTagLabel } from '@/service/gpu-instance/type';
import { useState, useEffect, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { useDebounce } from '@/hooks/useDebounce';
import { isDateInRange } from '@/utils/date';
import InstanceSearch from './instance-search';
import { useDemoMode } from '@/context/DemoModeContext';

// Type for grouped instances
type InstanceGroup = {
  project_name: string;
  instances: SSHInstance[];
  isGroup: boolean;
};

type FormSchemaFilter = {
  searchString: string;
  status: string[];
  date: DateRange | undefined;
};

const STORAGE_KEY_PREFIX = 'gpu-instances-status-filter';

const getStatusBadgeVariant = (status: SSHInstanceStatus) => {
  switch (status) {
    case SSHInstanceStatus.Running:
    case SSHInstanceStatus.SSHReady:
      return 'success';
    case SSHInstanceStatus.Pending:
      return 'warning';
    case SSHInstanceStatus.Stopped:
      return 'outline';
    case SSHInstanceStatus.Failed:
      return 'destructive';
    case SSHInstanceStatus.Completed:
    case SSHInstanceStatus.Cancelled:
    case SSHInstanceStatus.Terminated:
      return 'secondary';
    default:
      return 'default';
  }
};

const formatDate = (timestamp: number | null) => {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleString();
};

const getTagBadgeClass = (tag: InstanceTag) => {
  switch (tag) {
    case 'vllm':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
    case 'triton':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/30';
    case 'tao':
      return 'bg-green-500/10 text-green-600 border-green-500/30';
    case 'monai':
      return 'bg-red-500/10 text-red-600 border-red-500/30';
    case 'nemo':
      return 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30';
    case 'float16-training':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/30';
    default:
      return 'bg-gray-500/10 text-gray-600 border-gray-500/30';
  }
};

const GPUInstanceList = () => {
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { withDemoPrefix } = useDemoPath();
  const { data: instances, isLoading, isFetching, refetch } = useListSSHInstances();
  const { isDemoMode } = useDemoMode();

  // Load saved status filter from localStorage on mount
  const getSavedStatusFilter = (): string[] => {
    if (!workspaceId) return [];
    try {
      const saved = localStorage.getItem(`${STORAGE_KEY_PREFIX}-${workspaceId}`);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading status filter from localStorage:', error);
      return [];
    }
  };

  const [filter, setFilter] = useState<FormSchemaFilter>({
    searchString: '',
    status: getSavedStatusFilter(),
    date: undefined,
  });
  const debouncedSearchString = useDebounce(filter.searchString, 500);

  // Save status filter to localStorage whenever it changes
  useEffect(() => {
    if (!workspaceId) return;
    try {
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX}-${workspaceId}`,
        JSON.stringify(filter.status)
      );
    } catch (error) {
      console.error('Error saving status filter to localStorage:', error);
    }
  }, [filter.status, workspaceId]);

  // Filter instances based on search, status, and date
  const filteredInstances = instances?.filter((instance) => {
    // Case-insensitive substring search
    if (debouncedSearchString) {
      const searchLower = debouncedSearchString.toLowerCase();
      const taskIdLower = instance.task_id.toLowerCase();
      const projectNameLower = instance.project_name?.toLowerCase() || '';

      if (
        !taskIdLower.includes(searchLower) &&
        !projectNameLower.includes(searchLower)
      )
        return false;
    }
    if (filter.status.length > 0 && !filter.status.includes(instance.status))
      return false;
    if (instance.created_at && !isDateInRange(new Date(instance.created_at * 1000), filter.date)) return false;
    return true;
  });

  // Group instances by parent_task_id (for stop/resume tracking) or project_name (for MIG clusters in demo mode)
  const groupedInstances = useMemo(() => {
    if (!filteredInstances) return [];

    // Build a set of parent_task_ids to identify related instances
    const parentTaskIds = new Set<string>();
    filteredInstances.forEach((instance) => {
      if (instance.parent_task_id) {
        parentTaskIds.add(instance.parent_task_id);
      }
    });

    // For parent_task_id related instances, keep only the "best" one (running > pending > stopped)
    // First, group all parent-related instances
    const parentGroups: Record<string, SSHInstance[]> = {};
    const standaloneInstances: SSHInstance[] = [];

    filteredInstances.forEach((instance) => {
      if (instance.parent_task_id) {
        // This is a child instance
        const key = instance.parent_task_id;
        if (!parentGroups[key]) parentGroups[key] = [];
        parentGroups[key].push(instance);
      } else if (parentTaskIds.has(instance.task_id)) {
        // This is a parent instance (its task_id is referenced by others)
        const key = instance.task_id;
        if (!parentGroups[key]) parentGroups[key] = [];
        parentGroups[key].push(instance);
      } else {
        // Standalone instance
        standaloneInstances.push(instance);
      }
    });

    // Pick the best instance from each parent group:
    // 1. If there's a Running/SSHReady instance, show that
    // 2. If there's a Pending instance, show that
    // 3. Otherwise, show the most recently started instance
    const bestFromParentGroups = Object.values(parentGroups).map((instances) => {
      const runningInstance = instances.find(
        (i) => i.status === SSHInstanceStatus.Running || i.status === SSHInstanceStatus.SSHReady
      );
      if (runningInstance) return runningInstance;

      const pendingInstance = instances.find(
        (i) => i.status === SSHInstanceStatus.Pending
      );
      if (pendingInstance) return pendingInstance;

      // No active instance, return the most recent one by start_time, then end_time
      return instances.sort((a, b) => {
        const startDiff = (b.start_time || 0) - (a.start_time || 0);
        if (startDiff !== 0) return startDiff;
        return (b.end_time || 0) - (a.end_time || 0);
      })[0];
    });

    // Combine: best from parent groups + standalone instances
    // Sort by created_at descending (newest first)
    const deduplicatedInstances = [...bestFromParentGroups, ...standaloneInstances]
      .sort((a, b) => (b.created_at || 0) - (a.created_at || 0));

    // Now apply demo mode grouping (for MIG clusters) if needed
    if (!isDemoMode) {
      return deduplicatedInstances.map((instance): InstanceGroup => ({
        project_name: instance.project_name || instance.task_id,
        instances: [instance],
        isGroup: false,
      }));
    }

    // In demo mode, group by project_name for MIG clusters
    const groups: Record<string, SSHInstance[]> = {};
    deduplicatedInstances.forEach((instance) => {
      const key = instance.project_name || instance.task_id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(instance);
    });

    return Object.entries(groups)
      .map(([project_name, instances]): InstanceGroup => ({
        project_name,
        instances,
        isGroup: instances.length > 1,
      }))
      .sort((a, b) => (b.instances[0]?.created_at || 0) - (a.instances[0]?.created_at || 0));
  }, [filteredInstances, isDemoMode]);

  // Track expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (projectName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(projectName)) {
        next.delete(projectName);
      } else {
        next.add(projectName);
      }
      return next;
    });
  };

  // Calculate total active instances (Running or SSH Ready)
  const totalActiveInstances = instances?.filter(
    (instance) => instance.status === SSHInstanceStatus.Running || instance.status === SSHInstanceStatus.SSHReady
  ).length || 0;

  const isFoundInstances = instances && instances.length > 0;

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">GPU Instances</h1>
          <div className="rounded-full bg-primary/10 px-3 py-1">
            <span className="text-sm font-medium text-primary">
              {totalActiveInstances} Active
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`size-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Link to={withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.create(orgId, workspaceId))}>
            <Button size="sm">
              <PlusIcon className="size-4 mr-2" />
              Create Instance
            </Button>
          </Link>
        </div>
      </div>

      {isFoundInstances && (
        <div className="mb-6">
          <InstanceSearch filter={filter} onFilterChange={setFilter} />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Instances</CardTitle>
          <CardDescription>
            View and manage all your GPU instances
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : !instances || instances.length === 0 ? (
            <div className="text-center py-12">
              <Server className="size-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No instances yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first GPU instance to get started
              </p>
              <Link to={withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.create(orgId, workspaceId))}>
                <Button>
                  <PlusIcon className="size-4 mr-2" />
                  Create Instance
                </Button>
              </Link>
            </div>
          ) : !filteredInstances || filteredInstances.length === 0 ? (
            <div className="text-center py-12">
              <Server className="size-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No matching instances</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search or filter criteria
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Project Name</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead>GPU Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedInstances.map((group) => {
                  const isExpanded = expandedGroups.has(group.project_name);

                  // For single instances (not a group)
                  if (!group.isGroup) {
                    const instance = group.instances[0];
                    return (
                      <TableRow key={instance.task_id}>
                        <TableCell></TableCell>
                        <TableCell className="font-medium">
                          {instance.project_name}
                        </TableCell>
                        <TableCell>
                          {instance.instance_tag ? (
                            <Badge variant="outline" className={`text-xs ${getTagBadgeClass(instance.instance_tag)}`}>
                              {InstanceTagLabel[instance.instance_tag]}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>{instance.gpu_type}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(instance.status) as any}>
                            {SSHInstanceStatusLabel[instance.status] || instance.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {instance.instance_ip || '-'}
                        </TableCell>
                        <TableCell>
                          {formatDate(instance.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link
                            to={withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(
                              orgId,
                              workspaceId,
                              instance.task_id
                            ).overview)}
                          >
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // For grouped instances (MIG cluster)
                  const activeCount = group.instances.filter(
                    (i) => i.status === SSHInstanceStatus.Running || i.status === SSHInstanceStatus.SSHReady
                  ).length;

                  return (
                    <Collapsible key={group.project_name} open={isExpanded} onOpenChange={() => toggleGroup(group.project_name)} asChild>
                      <>
                        <CollapsibleTrigger asChild>
                          <TableRow className="cursor-pointer hover:bg-muted/50 bg-muted/30">
                            <TableCell className="w-8">
                              {isExpanded ? (
                                <ChevronDown className="size-4" />
                              ) : (
                                <ChevronRight className="size-4" />
                              )}
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {group.project_name}
                                <Badge variant="outline" className="text-xs">
                                  {group.instances.length} instances
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              {group.instances[0].instance_tag ? (
                                <Badge variant="outline" className={`text-xs ${getTagBadgeClass(group.instances[0].instance_tag)}`}>
                                  {InstanceTagLabel[group.instances[0].instance_tag]}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-muted-foreground text-sm">
                                {group.instances.length} GPUs
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="success" className="bg-green-500/10 text-green-600">
                                {activeCount} Active
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-sm text-muted-foreground">
                              {group.instances.length} IPs
                            </TableCell>
                            <TableCell>
                              {formatDate(group.instances[0].created_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleGroup(group.project_name); }}>
                                {isExpanded ? 'Collapse' : 'Expand'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        </CollapsibleTrigger>
                        <CollapsibleContent asChild>
                          <>
                            {group.instances.map((instance) => (
                              <TableRow key={instance.task_id} className="bg-background border-l-2 border-l-primary/30">
                                <TableCell></TableCell>
                                <TableCell className="font-medium pl-6">
                                  <span className="text-muted-foreground">└</span>{' '}
                                  <span className="text-sm">{instance.task_name || instance.task_id}</span>
                                </TableCell>
                                <TableCell>
                                  {instance.instance_tag ? (
                                    <Badge variant="outline" className={`text-xs ${getTagBadgeClass(instance.instance_tag)}`}>
                                      {InstanceTagLabel[instance.instance_tag]}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground text-sm">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="secondary" className="font-mono text-xs">
                                    {instance.gpu_type?.replace('NVIDIA H200 141GB ', '')}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={getStatusBadgeVariant(instance.status) as any}>
                                    {SSHInstanceStatusLabel[instance.status] || instance.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="font-mono text-sm">
                                  {instance.instance_ip || '-'}
                                </TableCell>
                                <TableCell>
                                  {formatDate(instance.created_at)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Link
                                    to={withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(
                                      orgId,
                                      workspaceId,
                                      instance.task_id
                                    ).overview)}
                                  >
                                    <Button variant="ghost" size="sm">
                                      View
                                    </Button>
                                  </Link>
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GPUInstanceList;
